<template>
  <div class="adsetSectionContainer">
    <div class="ad-set-details-container w-full px-8 bg-white">
      <div class="font-normal text-2xl text-black" slot="header">
        <div class="flex">
          <h3 class="mb-2">{{ $t('Ad Set Details') }}</h3>
          <speakol-tooltip  class="adsets-tooltip" :tooltipRedirect="'https://speakol.zendesk.com/hc/en-us/articles/360016737919-Ad-Set-Targeting-'" :tooltipAction="'Learn more here'" :tooltipText="'Each set is unique. You can customize each ad set by adjusting the location, audience, ISP, and publishers.'"></speakol-tooltip>
        </div>
        <p class="section-desc  mb-6 text-gray-500 font-normal">
          {{ $t("Please fill in your adset details.") }}
        </p>
      </div>

      <AdsetDetails v-model="value" :adsetIndex="adsetIndex" :serverErrors="serverErrors" />
    </div>

    <div v-if="advertisingChannel.supports_audience_creation" class="audience-section-container w-full px-8 py-6 bg-white">
      <div class="" slot="header">
        <div class="flex">
          <h3 :class="{ 'text-danger': audienceTargeting.hasErrors, 'text-black': !audienceTargeting.hasErrors }" class="mb-2 font-normal text-2xl">{{ $t('Audience Targeting') }}</h3>
        </div>
        <p class="section-desc mb-6 text-gray-500 font-normal">
          {{ $t("You can customize the characteristics of the audience you want to target. You have to fill at least 1 section to create your new audience.") }}
        </p>
      </div>

      <AudienceSection v-model="value" :adsetIndex="adsetIndex" :serverErrors="serverErrors"
        @audience:selected="audienceSelected" />
    </div>

    <vs-collapse ref="adsetSectionCollapse" class="p-0 bg-white" v-if="!fillingAudienceData" accordion>
      <!-- Location Taregting Section -->
      <div class="divider"></div>
      <vs-collapse-item class="sm:p-4" v-if="advertisingChannel.supports_location_targeting">
        <div
          @click="$gtag.event('Step3_LocationTab')" class="font-normal text-2xl text-danger"
          slot="header">
          <h3 :class="[locationsHasErrors.hasErrors || saveAuidenceLocation.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Location Targeting') }}</h3>
          <p class="section-desc  text-gray-500 font-normal">
            {{ $t("You can target audience from all around the world. The Sky is the limit.") }}
          </p>
        </div>

        <LocationTargeting :advertisingChannel="advertisingChannel" :acceptance-status="mergedAcceptanceAdsStatus" :editing="editing" v-model="value.locations" />
      </vs-collapse-item>

      <div class="divider" v-if="showKeywordsSection"></div>
      <!-- Keywords Section -->
      <vs-collapse-item class="sm:p-4" v-if="showKeywordsSection">
        <div
          class="font-normal text-2xl"
          slot="header">
          <h3 :class="[keywordsHasErrors.hasErrors || saveAuidenceKeywords.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Keywords Targeting') }}</h3>
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("Please fill in your targeted keywords.") }}
          </p>
        </div>

        <Keywords v-model="value.keywords" />
      </vs-collapse-item>
      <div class="divider" v-if="showKeywordsSection"></div>

      <vs-collapse-item class="sm:p-4" v-if="isTargetPlatformSectionShown && advertisingChannel.supports_native_publisher_targeting">
        <div
          @click="$gtag.event('Step3_PublishersTab')"
          class="font-normal text-2xl"
          slot="header">
          <h3 :class="[targetPlatformsHasErrors.hasErrors || saveAuidencePublishers.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Publishers') }}</h3>
          <p class="section-desc  text-gray-500 font-normal">
            {{ $t("Target some of the regions most premium websites.") }}
          </p>
        </div>

        <TargetPlatforms v-model="value.target_platforms" :industry_id="industry_id"
          :account_id="value.campaign_account_id || this.active_account_id" :editing="editing || value.is_duplicating"
          :adsetIndex="adsetIndex" :isRefillingFromAnAudience="!!this.value.audience_id" />
      </vs-collapse-item>
      <div class="divider" v-if="isTargetPlatformSectionShown && advertisingChannel.supports_native_publisher_targeting"></div>

      <vs-collapse-item class="sm:p-4" v-if="advertisingChannel.supports_meta_detailed_targeting">
        <div
          @click="$gtag.event('Step3_detailedMetaTab')" class="font-normal text-2xl text-danger"
          slot="header">
          <h3 :class="[metaTargetingHasErrors.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Detailed Targeting') }}</h3>
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("We may deliver ads beyond your audience for eligible ad objectives if it's likely to improve performance.") }}
          </p>
        </div>

        <TargetDetailedMeta v-model="value.meta_detailed_targeting" :editing="editing || value.is_duplicating"
          :adsetIndex="adsetIndex"  :acceptance-status="mergedAcceptanceAdsStatus"/>
      </vs-collapse-item>

      <!-- Category serction -->
      <vs-collapse-item class="sm:p-4" v-if="$hasFeature('content-categories') && advertisingChannel.supports_native_category_targeting">

        <!-- Card Header Section -->
        <div class="font-normal text-2xl"
          slot="header">
          <h3 :class="[contentCategoriesHasErrors.hasErrors || saveAuidenceCategory.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Categories') }}</h3>
          <p class="section-desc  text-gray-500 font-normal">
            {{ $t("Target the categories you would like to feature your ads alongside. ") }}
          </p>
        </div>

        <!-- Card Component Section -->
        <ContentCategories v-model="value.content_categories" :editing="editing || value.is_duplicating" :adsetIndex="adsetIndex"/>

      </vs-collapse-item>

      <div class="divider" v-if="$hasFeature('content-categories') && advertisingChannel.supports_native_category_targeting"></div>

      <vs-collapse-item class="sm:p-4" v-if="advertisingChannel.supports_device_targeting">
        <div @click="$gtag.event('Step3_DevicesTab')" class="font-normal text-2xl"
          slot="header">
          <h3 :class="[targetDevicesHasErrors.hasErrors || saveAuidenceTargetDevices.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Targeting Devices') }}</h3>
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("Reach audience across all platforms.") }}
          </p>
        </div>
        <TargetDevices v-model="value.target_devices" :adsetIndex="adsetIndex" :objective="objective" />
      </vs-collapse-item>

      <vs-collapse-item class="sm:p-4" v-if="advertisingChannel.supports_gender_targeting">
        <div @click="$gtag.event('Step3_GendersTab')" class="font-normal text-2xl"
             slot="header">
          <h3 :class="[targetGendersHasErrors.hasErrors || saveAuidenceTargetDevices.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Targeting Genders') }}</h3>
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("Target the genders.") }}
          </p>
        </div>
        <TargetGenders v-model="value.target_genders" :acceptance-status="mergedAcceptanceAdsStatus" :advertising-channel="advertisingChannel" :editing="editing" :adsetIndex="adsetIndex" :objective="objective" />
      </vs-collapse-item>

      <vs-collapse-item class="sm:p-4" v-if="advertisingChannel.supports_age_range_targeting">
        <div @click="$gtag.event('Step3_AgeRangeTab')" class="font-normal text-2xl"
             slot="header">
          <h3 :class="[targetAgeRangeHasErrors.hasErrors || saveAuidenceTargetDevices.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Targeting Age') }}</h3>
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("Target the age range.") }}
          </p>
        </div>
        <TargetAgeRange v-model="value.target_age_range" :acceptance-status="mergedAcceptanceAdsStatus" :advertising-channel="advertisingChannel" :editing="editing" :adsetIndex="adsetIndex"/>
      </vs-collapse-item>

      <div class="divider" v-if="advertisingChannel.supports_frequency_capping_targeting" ></div>

      <vs-collapse-item v-if="advertisingChannel.supports_frequency_capping_targeting" class="sm:p-4 specific-class">
        <div @click="$gtag.event('Step3_AdvTab')" class="font-normal text-2xl"
          slot="header">
          <h3 :class="[advancedOptionsHasErrors.hasErrors || saveAuidenceAdvancedOptions.hasErrors ? 'text-danger' : 'text-black' ]" class="font-normal text-2xl">{{ $t('Advanced Options') }}</h3>
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("Specify the number of times you want you ad to be displayed.") }}
          </p>
        </div>

        <AdvancedOptions v-model="value" :advertising-channel="advertisingChannel" :adsetIndex="adsetIndex" />
      </vs-collapse-item>

      <div  class="save-audience-button" v-if="value.new_audience && advertisingChannel.supports_audience_creation">
        <button id="create-campaign-s3-save-audience-button" @click="saveAudience">
          {{ $t('Save Audience') }}
        </button>
        <div class="save-audience-desc-parent">
          <p class="save-audience-desc">
            {{ $t('If you want, you can save the audience you created.') }}
          </p>
        </div>
      </div>
    </vs-collapse>

    <br v-if="$vErrors.hasMany([errors, serverErrors], adsetErrors)">
    <span class="text-danger" v-if="$vErrors.hasMany([errors, serverErrors], adsetErrors)">
      <p v-if="!$vErrors.has([errors, serverErrors], `step3.adsets.${adsetIndex}.audience`)">
        *You need to fill in the missing feilds correctly before moving on to the next step.
      </p>

      <ul>
        <template v-for="name in adsetErrors">
          <li v-if="$vErrors.has([errors, serverErrors], name)" :key="name">
            {{ $vErrors.first([errors, serverErrors], name) }}
          </li>
        </template>
      </ul>
    </span>

    <SpeakolNotificationToaster :show="toaster.show" :message="toaster.message" :icon="toaster.icon"
      :closeButtonText="toaster.closeButtonText" @close="toaster.show=false" />
  </div>
</template>

<script>
import AdsetDetails from './components/AdsetDetails.vue'
import AudienceSection from './components/AudienceSection.vue'
import TargetPlatforms from './components/TargetPlatforms.vue'
import AdvancedOptions from './components/AdvancedOptions.vue'
import ContentCategories from './components/ContentCategories.vue'
import TargetDevices from './components/TargetDevices.vue'
import Keywords from './components/Keywords.vue'
import LocationTargeting from './components/LocationTargeting.vue'
import SpeakolNotificationToaster from '@/components/SpeakolNotificationToaster'
import SpeakolTooltip from '@/components/speakol-template/SpeakolTooltip.vue'
import { get } from 'lodash'
import { ignoreTargeting } from '@/utils/IgnoreTargeting.js'
import TargetGenders from '@/views/campaigns/components/steps/components/AdsetContent/components/TargetGenders.vue'
import TargetAgeRange from '@/views/campaigns/components/steps/components/AdsetContent/components/TargetAgeRange.vue'
import TargetDetailedMeta from '@/views/campaigns/components/steps/components/AdsetContent/components/TargetDetailedMeta.vue'

export default {
  props: {
    value: {
      type: Object,
      required: true
    },
    adsetIndex: {
      type: Number,
      required: true
    },
    serverErrors: {
      required: true
    },
    advertisingChannel: {
      type: Object,
      required: true
    },
    objective: {
      type: String,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    industry_id: {
      type: Number,
      required: true
    },
    editing: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  watch: {
    'value': {
      handler (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    'value.new_audience' () {
      this.clearAudienceErrors()
    },
    'value.locations': {
      handler () {
        this.$emit('fireTrafficEstimator', this.adsetIndex)

        if (this.advertisingChannel.supports_reach_estimate) {
          this.$emit('adsetCountriesChanged', {'adsetIndex': this.adsetIndex, 'countries': this.value.locations.countries})
        }
      },
      deep: true
    },
    'value.target_platforms': {
      handler () {
        this.$emit('fireTrafficEstimator', this.adsetIndex)
      },
      deep: true
    },
    'value.content_categories': {
      handler () {
        this.$emit('fireTrafficEstimator', this.adsetIndex)
      },
      deep: true
    },
    'value.target_devices': {
      handler () {
        this.$emit('fireTrafficEstimator', this.adsetIndex)
      },
      deep: true
    }
  },
  components: {
    TargetDetailedMeta,
    TargetAgeRange,
    TargetGenders,
    AdsetDetails,
    AudienceSection,
    ContentCategories,
    LocationTargeting,
    AdvancedOptions,
    Keywords,
    TargetDevices,
    TargetPlatforms,
    SpeakolNotificationToaster,
    SpeakolTooltip
  },
  computed: {
    active_account_id () {
      return parseInt(localStorage.getItem('activeAccountId'))
    },

    mergedAcceptanceAdsStatus () {
      return !!this.value.ads.map(ad => ad.acceptance_status).reduce((acc, status) => acc || status, false)
    },

    adsetErrors () {
      return [
        ...this.locationsHasErrors.names,
        ...this.metaTargetingHasErrors.names,
        ...this.detailsHasErrors.names,
        ...this.keywordsHasErrors.names,
        ...this.targetPlatformsHasErrors.names,
        ...this.targetDevicesHasErrors.names,
        ...this.targetGendersHasErrors.names,
        ...this.targetAgeRangeHasErrors.names,
        ...this.advancedOptionsHasErrors.names,
        ...this.contentCategoriesHasErrors.names,
        ...[`step3.adsets.${this.adsetIndex}.audience`]
      ]
    },
    detailsHasErrors () {
      const names = [
        `step3.adsets.${this.adsetIndex}.name`,
        `step3.adsets.${this.adsetIndex}.language`
      ]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    contentCategoriesHasErrors () {
      const names = [`step3.adsets.${this.adsetIndex}.content_categories`]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    locationsHasErrors () {
      const names = [
        `step3.adsets.${this.adsetIndex}.locations.countries`,
        `step3.adsets.${this.adsetIndex}.locations.cities`
      ]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    metaTargetingHasErrors () {
      const names = [`step3.adsets.${this.adsetIndex}.meta_detailed_targeting`]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    keywordsHasErrors () {
      const names = [`step3.adsets.${this.adsetIndex}.keywords`]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    targetPlatformsHasErrors () {
      const names = [
        `step3.adsets.${this.adsetIndex}.target_platforms.websites`,
        `step3.adsets.${this.adsetIndex}.target_platforms.apps`,
        `step3.adsets.${this.adsetIndex}.target_platforms.include_new_platforms`,
        `step3.adsets.${this.adsetIndex}.target_platforms`
      ]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    targetDevicesHasErrors () {
      const names = [`step3.adsets.${this.adsetIndex}.target_devices`]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    targetGendersHasErrors () {
      const names = [`step3.adsets.${this.adsetIndex}.target_genders`]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    targetAgeRangeHasErrors () {
      const names = [`step3.adsets.${this.adsetIndex}.target_age_range`]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    advancedOptionsHasErrors () {
      const names = [
        `step3.adsets.${this.adsetIndex}.isps`,
        `step3.adsets.${this.adsetIndex}.frequency_capping`
      ]

      return {
        hasErrors: this.$vErrors.hasMany([this.errors, this.serverErrors], names),
        names
      }
    },
    isTargetPlatformSectionShown () {
      return !ignoreTargeting(this.objective, this.type)
    },
    showKeywordsSection () {
      return this.type === this.$constants.CAMPAIGN_TYPES_NAMES.INLINE
    },

    disabledDevices () {
      return this.objective === this.$constants.CAMPAIGN_OBJECTIVES_NAMES.LEAD_FORM ? [this.$constants.DEVICES_IDS.DESKTOP] : []
    }
  },
  data () {
    return {
      audienceTargeting:{
        hasErrors: false
      },
      saveAuidenceLocation: {
        hasErrors : false
      },
      saveAuidenceKeywords: {
        hasErrors : false
      },
      saveAuidencePublishers: {
        hasErrors : false
      },
      saveAuidenceCategory: {
        hasErrors : false
      },
      saveAuidenceTargetDevices: {
        hasErrors : false
      },
      saveAuidenceAdvancedOptions: {
        hasErrors : false
      },
      toaster: {
        show: false,
        message: this.$t('Audience has been saved in audiences list.'),
        icon: require('@/assets/images/success.svg'),
        closeButtonText: 'X'
      },
      fillingAudienceData: false
    }
  },
  mounted () {
    // to activate the firstCollapseItem
    setTimeout(() => {
      if (this.$refs.firstCollapseItem && this.$refs.firstCollapseItem.$el) {
        this.$refs.firstCollapseItem.$el.children[0].click()
      }
    }, 0)

    this.$root.$on('step3-resolved', () => {
      this.clearAudienceErrors()
    })
  },
  beforeDestroy () {
    if (this.$refs.adsetSectionCollapse.$children.length) {
      for (const item of this.$refs.adsetSectionCollapse.$children) {
        window.removeEventListener('resize', item.changeHeight)
      }
    }
  },

  methods: {
    async saveAudience () {
      this.$gtag.event('Step3_SaveAudiencebut')
      try {
        const form = {
          name: this.value.new_audience_name,
          locations: this.value.locations,
          isps: [],
          target_devices: this.value.target_devices.filter(id => !this.disabledDevices.includes(id)),
          frequency_capping: this.value.frequency_capping,
          content_categories: this.value.content_categories
        }

        if (this.type === 'Inline') {
          form.keywords = this.value.keywords
        }

        if (this.isTargetPlatformSectionShown) {
          form.target_platforms = this.value.target_platforms
        }

        const response = await window.axios.post('/audience/create', form)

        this.$root.$emit('audience:created', response.data.payload)

        this.clearAudienceErrors()

        this.toaster.show = true
        this.toaster.message = `${this.value.new_audience_name} ${this.$t('has been saved in audiences list.')}`
        setTimeout(() => {
          this.toaster.show = false
        }, 4000)
      } catch (err) {
        this.clearAudienceErrors()

        if ('name' in err.response.data.errors) {
          this.errors.add({
            field: `step3.adsets.${this.adsetIndex}.new_audience_name`,
            msg: err.response.data.errors.name.join(', ')
          })
        }

        if ('locations.countries' in err.response.data.errors) {
          this.audienceTargeting.hasErrors = true
          this.saveAuidenceLocation.hasErrors = true
          this.saveAuidenceKeywords.hasErrors = true
          this.saveAuidencePublishers.hasErrors = true
          this.saveAuidenceCategory.hasErrors = true
          this.saveAuidenceTargetDevices.hasErrors = true
          this.saveAuidenceAdvancedOptions.hasErrors = true

          this.errors.add({
            field: `step3.adsets.${this.adsetIndex}.audience`,
            msg: 'You have to fill at least 1 section to create your new audience.'
          })
        }
      }
    },
    clearAudienceErrors () {
      this.errors.remove(`step3.adsets.${this.adsetIndex}.new_audience_name`)
      this.errors.remove(`step3.adsets.${this.adsetIndex}.audience`)
      this.audienceTargeting.hasErrors = false
      this.saveAuidenceLocation.hasErrors = false
      this.saveAuidenceKeywords.hasErrors = false
      this.saveAuidencePublishers.hasErrors = false
      this.saveAuidenceCategory.hasErrors = false
      this.saveAuidenceTargetDevices.hasErrors = false
      this.saveAuidenceAdvancedOptions.hasErrors = false
    },
    audienceSelected (audience) {
      if (!audience) {
        return
      }

      this.fillingAudienceData = true

      this.value.is_duplicating = true
      // this.value.audience_id = audience.id

      this.value.target_devices = audience.target.target_devices.filter((id) => !this.disabledDevices.includes(id))

      this.value.locations.countries = audience.target.locations.countries
      this.value.locations.cities = audience.target.locations.cities

      this.value.isps = []

      if (this.type === 'Inline') {
        this.value.keywords = get(audience.target, 'keywords', [])
      }

      if (this.isTargetPlatformSectionShown) {
        this.value.target_platforms.apps = get(audience.target, 'target_platforms.apps', [])
        this.value.target_platforms.websites = get(audience.target, 'target_platforms.websites', [])
        this.value.target_platforms.include_new_platforms = get(audience.target,
          'target_platforms.include_new_platforms', true)
      }

      if (this.isTargetPlatformSectionShown) {
        this.value.target_platforms.include_new_platforms = true
      }

      this.value.frequency_capping = audience.target.frequency_capping
      this.value.content_categories = audience.target.content_categories

      setTimeout(() => {
        this.fillingAudienceData = false
      }, 0)
    }
  }
}

</script>

<style lang="scss">
  .open-item {
    .vs-collapse-item--content {
      overflow: visible !important;
    }
  }

  .adsets-tooltip {
    i {
      position: relative;
      top: -9px;
    }
  }

  .adsetSectionContainer,
  .ad-preview-container {
    .specific-class {
      border-bottom-right-radius: 15px;
      border-bottom-left-radius: 15px;

      .tree-children {
        li {
          .tree-content {
            padding-left: 25px !important;
          }
        }
      }
    }

    .section-desc {
      margin-top: 4px;
      font-size: 14px;
    }

    .divider {
      width: 93%;
      border: 1px solid rgba(189, 195, 202, 0.215527);
      margin: 0 auto;
    }

    .vs-collapse-item {
      border-bottom: 0 !important;
    }

    .ad-set-details-container {
      padding-top: 20px;
      border-bottom-right-radius: 15px;
      border-bottom-left-radius: 15px;
    }

    .audience-section-container {
      border-top-left-radius: 15px;
      border-top-right-radius: 15px;
    }

    .save-audience-button {
      button {
        background-color: white;
        border: 1px solid #0092d7;
        color: #0092d7;
        box-shadow: none;
        width: 180px;
        height: 40px;
        cursor: pointer;
        margin-left: 30px;
        border-radius: 3px;
      }

      .save-audience-desc-parent {
        margin-top: 20px;
        margin-left: 30px;
        padding-bottom: 30px;

        .save-audience-desc {
          color: #0092d7;
          font-weight: 300;
          font-size: 14px;
        }
      }
    }

    .adsetSectionContainer header {
      cursor: pointer;
    }

    .adsetSectionContainer header.vs-collapse-item--header span i {
      font-size: 3rem;
    }

    .adsetSectionContainer header.vs-collapse-item--header div {
      font-size: 1.5rem;
    }

    .adsetSectionContainer .vs-collapse-item {
      background-color: white;
      cursor: default;
    }

    .adsetSectionContainer .vx-card .vx-card__header .vx-card__actions svg {
      height: 4rem;
      width: 4rem;
    }

    .treeContainer {
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 10px;
    }

    [dir] .autocomplete-input {
      background-color: white;
      border: 1px solid rgba(0, 0, 0, 0.15);
    }

    [dir] .autocomplete-input:focus,
    [dir] .autocomplete-input[aria-expanded=true] {
      box-shadow: none;
      border: 1px solid rgba(0, 0, 0, 0.15);
    }

    @media (max-width: 576px) {
      .adsetSectionContainer header.vs-collapse-item--header div {
        font-size: 1.2rem;
      }

      .vuesax-app-is-ltr .vs-collapse.default .open-item .vs-collapse-item--icon-header {
        margin-top: -43px;
      }

      .adsetSectionContainer header.vs-collapse-item--header span i {
        font-size: 2rem;
      }
    }

    /* For tags span */
    .span-tag {
      border: 1px solid #6dc5d0;
      box-sizing: border-box;
      border-radius: 21px;
      padding: 6px 10px;
      font-weight: 500;
      color: #6dc5d0;
      cursor: pointer;
    }

    .span-tag:hover {
      background-color: rgba(109, 197, 208, 0.15);
    }

    .span-tag-active {
      background-color: #6dc5d0;
      color: white;
    }

    .span-tag-active:hover {
      background-color: #6dc5d0;
    }

    @media (max-width: 576px) {
      .span-tag {
        overflow-x: scroll;
        display: inline-block;
      }

      .section-desc {
        width: 90%;
      }
    }

    /* For the checkbox tree */
    .tree-checkbox.checked,
    .tree-checkbox.indeterminate {
      border: 2px solid #6dc5d0 !important;
      background: #6dc5d0;
      border-radius: 2px !important;
      width: 16px;
      height: 16px;
    }

    .tree-checkbox {
      width: 16px !important;
      border-radius: 2px !important;
      height: 16px !important;
    }

    .tree-checkbox.checked::after {
      left: 4px !important;
      top: -1px !important;
      height: 9px !important;
      width: 4px !important;
    }

    .tree-node:not(.selected) > .tree-content:hover {
      background-color: #e9f6f8;
    }

    .tree-node.selected > .tree-content {
      background: #e9f6f8;
    }

    .tree-content {
      padding-left: 0 !important;
    }

    .first-collapse-style {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: 15px;
      border-bottom-left-radius: 15px;
    }

    .collapse-style {
      border-radius: 15px;
    }

    .card-error {
      border: 1px solid rgba(224, 32, 32, 0.5);
    }

    .autocomplete-input,
    .autocomplete-input:focus,
    .autocomplete-input[aria-expanded=true] {
      background-color: white !important;
      border: 1px solid rgba(0, 0, 0, 0.15) !important;
      box-shadow: none;
    }
  }

</style>
